import mixpanel, { type Mixpanel } from 'mixpanel-browser';
import type { UserTraits } from '@segment/analytics-next';

import {
  LOCKED_MIXPANEL_CONFIG,
  LOCKED_SESSION_REPLAY_CONFIG,
  MIXPANEL_DEVICE_ID_PREFIX,
  NO_MARKETING_PROPERTIES_CONFIG,
  PERSISTED_REFERRER_PROPERTIES,
  type MixpanelSessionReplayOptions,
  type MixpanelTransportOptions,
} from '../config/mixpanel-config';

import type { AnalyticsTransport } from './analytics-transport';

type TMixpanelWithRecordingInit = Mixpanel & { __get_recording_init_promise?: () => Promise<unknown> | undefined };

const dropPersistedReferrer = (instance: Mixpanel) => PERSISTED_REFERRER_PROPERTIES.forEach((property) => instance.unregister(property));

const isSampledIn = (sessionReplay?: MixpanelSessionReplayOptions) => !!sessionReplay && Math.random() * 100 < sessionReplay.sampleRate;

export class MixpanelTransport implements AnalyticsTransport {
  private readonly instance: TMixpanelWithRecordingInit;
  private readonly isSessionReplaySampled: boolean;
  private deviceId?: string;
  private userId?: string;

  constructor({
    token,
    apiHost,
    instanceName,
    sessionReplay,
    disablePersistence,
    apiTransport,
    disableMarketingProperties,
  }: MixpanelTransportOptions) {
    this.isSessionReplaySampled = isSampledIn(sessionReplay);
    this.instance = mixpanel.init(
      token,
      {
        ...LOCKED_MIXPANEL_CONFIG,
        ...(sessionReplay && {
          ...LOCKED_SESSION_REPLAY_CONFIG,
          record_unmask_text_selector: sessionReplay.unmaskTextSelectors,
          record_block_selector: sessionReplay.blockSelector,
        }),
        ...(disablePersistence && { disable_persistence: true }),
        ...(apiTransport && { api_transport: apiTransport }),
        ...(disableMarketingProperties && NO_MARKETING_PROPERTIES_CONFIG),
        api_host: apiHost,
        persistence_name: instanceName,
        loaded: dropPersistedReferrer,
      },
      instanceName,
    );
  }

  track(eventName: string, properties?: Record<string, unknown>) {
    this.instance.track(eventName, { ...properties, ...this.identityProperties() });
  }

  page(name: string, properties?: Record<string, unknown>) {
    this.instance.track(`Viewed ${name}`, { ...properties, ...this.identityProperties() });
  }

  identify(userId: string, traits?: UserTraits) {
    this.userId = userId;
    this.instance.identify(userId);
    if (traits) {
      this.instance.people.set(traits);
    }
  }

  group() {}

  async getAnonymousId() {
    return null;
  }

  reset() {
    this.deviceId = undefined;
    this.userId = undefined;
    this.instance.reset();
  }

  setDeviceId(id: string) {
    this.deviceId = id;

    if (this.instance.get_property('$device_id') === id) {
      this.userId = this.instance.get_property('$user_id') ?? undefined;
      return;
    }

    this.userId = undefined;
    this.instance.reset();
    this.instance.register({ distinct_id: `${MIXPANEL_DEVICE_ID_PREFIX}${id}`, $device_id: id });
  }

  startSessionReplay() {
    if (!this.isSessionReplaySampled) return;

    this.instance.start_session_recording();
  }

  stopSessionReplay() {
    this.instance.stop_session_recording();
    this.stopSessionReplayAfterInitCheck().catch((error) => {
      console.warn(`[analytics] stopSessionReplay failed: ${error instanceof Error ? error.message : String(error)}`);
    });
  }

  private async stopSessionReplayAfterInitCheck() {
    await this.instance.__get_recording_init_promise?.();
    this.instance.stop_session_recording();
  }

  private identityProperties() {
    const distinctId = this.userId ?? (this.deviceId && `${MIXPANEL_DEVICE_ID_PREFIX}${this.deviceId}`);

    return {
      ...(this.deviceId && { $device_id: this.deviceId }),
      ...(this.userId && { $user_id: this.userId }),
      ...(distinctId && { distinct_id: distinctId }),
    };
  }
}
